import { useMemo } from 'react';
import { AdDataFreshnessNote } from './AdDataFreshnessNote';
import { useDeliverySiteScope } from '../hooks/useDeliverySiteScope';
import { PeriodRangePicker, type PeriodRangeGrain } from './PeriodRangePicker';
import type { SelectOption } from './MultiSelect';

export type MarketingTimeGrain = 'day' | 'week' | 'month' | 'quarter' | 'halfYear' | 'half_year' | 'year';

export interface MarketingTimePreset<T extends string = string> {
  value: T;
  label: string;
}

interface MarketingTimeRangeFilterProps<TGrain extends string, TPreset extends string> {
  grain: TGrain;
  grains: readonly { value: TGrain; label: string }[];
  onGrainChange: (grain: TGrain) => void;
  preset: TPreset;
  presets: readonly MarketingTimePreset<TPreset>[];
  onPresetChange: (preset: TPreset) => void;
  start: string;
  end: string;
  onRangeChange: (range: { start: string; end: string }) => void;
  max: string;
  customPreset: TPreset;
  grainLabel?: string;
  rangeLabel?: string;
  variant?: 'default' | 'reference';
  alwaysShowDatePicker?: boolean;
}

function parseYmd(value: string) {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) return null;
  return new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])));
}

function formatYmd(date: Date) {
  return `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}-${String(date.getUTCDate()).padStart(2, '0')}`;
}

// Calendar shortcuts use the same date string as the calling report, so
// local-date and UTC-based reports keep their existing day boundary.
export function getCurrentCalendarRange(preset: 'thisWeek' | 'thisMonth', today: string) {
  const start = parseYmd(today);
  if (!start) return { start: today, end: today };
  if (preset === 'thisWeek') start.setUTCDate(start.getUTCDate() - ((start.getUTCDay() + 6) % 7));
  else start.setUTCDate(1);
  return { start: formatYmd(start), end: today };
}

function canonicalGrain(grain: string): PeriodRangeGrain {
  return grain === 'half_year' ? 'halfYear' : grain as PeriodRangeGrain;
}

function periodStart(value: string, grain: PeriodRangeGrain) {
  const date = parseYmd(value);
  if (!date) return value;
  switch (grain) {
    case 'week': date.setUTCDate(date.getUTCDate() - ((date.getUTCDay() + 6) % 7)); break;
    case 'month': date.setUTCDate(1); break;
    case 'quarter': date.setUTCMonth(Math.floor(date.getUTCMonth() / 3) * 3, 1); break;
    case 'halfYear': date.setUTCMonth(date.getUTCMonth() < 6 ? 0 : 6, 1); break;
    case 'year': date.setUTCMonth(0, 1); break;
    default: break;
  }
  return formatYmd(date);
}

function periodEnd(value: string, grain: PeriodRangeGrain) {
  const date = parseYmd(periodStart(value, grain));
  if (!date) return value;
  switch (grain) {
    case 'week': date.setUTCDate(date.getUTCDate() + 6); break;
    case 'month': date.setUTCMonth(date.getUTCMonth() + 1, 0); break;
    case 'quarter': date.setUTCMonth(date.getUTCMonth() + 3, 0); break;
    case 'halfYear': date.setUTCMonth(date.getUTCMonth() + 6, 0); break;
    case 'year': date.setUTCMonth(date.getUTCMonth() + 12, 0); break;
    default: break;
  }
  return formatYmd(date);
}

// Expands a rolling/custom range to the full calendar periods it intersects.
// For example, a weekly view for 2026-09-01 through 2026-09-07 becomes
// 2026-08-31 through 2026-09-13, so neither week is reported partially.
export function alignMarketingDateRange(range: { start: string; end: string }, grain: MarketingTimeGrain | string) {
  const normalizedGrain = canonicalGrain(grain);
  const start = parseYmd(range.start);
  const end = parseYmd(range.end);
  if (!start || !end || start > end || normalizedGrain === 'day') return range;
  return {
    start: periodStart(range.start, normalizedGrain),
    end: periodEnd(range.end, normalizedGrain),
  };
}

function previousPeriodStart(value: string, grain: PeriodRangeGrain) {
  const date = parseYmd(value);
  if (!date) return value;
  switch (grain) {
    case 'week': date.setUTCDate(date.getUTCDate() - 7); break;
    case 'month': date.setUTCMonth(date.getUTCMonth() - 1, 1); break;
    case 'quarter': date.setUTCMonth(date.getUTCMonth() - 3, 1); break;
    case 'halfYear': date.setUTCMonth(date.getUTCMonth() - 6, 1); break;
    case 'year': date.setUTCFullYear(date.getUTCFullYear() - 1, 0, 1); break;
    default: date.setUTCDate(date.getUTCDate() - 1); break;
  }
  return formatYmd(date);
}

function isoWeekOf(date: Date) {
  const thursday = new Date(date);
  thursday.setUTCDate(thursday.getUTCDate() + 3 - ((thursday.getUTCDay() + 6) % 7));
  const firstThursday = new Date(Date.UTC(thursday.getUTCFullYear(), 0, 4));
  firstThursday.setUTCDate(firstThursday.getUTCDate() + 3 - ((firstThursday.getUTCDay() + 6) % 7));
  return { year: thursday.getUTCFullYear(), week: 1 + Math.round((thursday.getTime() - firstThursday.getTime()) / (7 * 24 * 60 * 60 * 1000)) };
}

function formatPeriod(value: string, grain: PeriodRangeGrain) {
  const date = parseYmd(value);
  if (!date) return value;
  if (grain === 'week') {
    const end = new Date(date);
    end.setUTCDate(end.getUTCDate() + 6);
    const { year, week } = isoWeekOf(date);
    return `${year}年第${week}周（${formatYmd(date)} 至 ${formatYmd(end)}）`;
  }
  if (grain === 'month') return value.slice(0, 7);
  if (grain === 'quarter') return `${date.getUTCFullYear()}年第${Math.floor(date.getUTCMonth() / 3) + 1}季度`;
  if (grain === 'halfYear') return `${date.getUTCFullYear()}年${date.getUTCMonth() < 6 ? '上半年' : '下半年'}`;
  if (grain === 'year') return String(date.getUTCFullYear());
  return value;
}

function periodOptions(grain: PeriodRangeGrain, max: string): SelectOption[] {
  if (grain === 'day') return [];
  const result: SelectOption[] = [];
  let value = periodStart(max, grain);
  const minimum = grain === 'week' ? '' : '2025-01-01';
  const limit = grain === 'week' ? 110 : Number.POSITIVE_INFINITY;
  for (let index = 0; index < limit && (!minimum || value >= minimum); index += 1) {
    result.push({ value, label: formatPeriod(value, grain) });
    value = previousPeriodStart(value, grain);
  }
  return result;
}

export function MarketingTimeRangeFilter<TGrain extends string, TPreset extends string>({
  grain, grains, onGrainChange, preset, presets, onPresetChange, start, end, onRangeChange, max, customPreset,
  grainLabel = '时间粒度', rangeLabel = '时间范围', variant = 'default', alwaysShowDatePicker = false,
}: MarketingTimeRangeFilterProps<TGrain, TPreset>) {
  const pickerGrain = canonicalGrain(grain);
  const options = useMemo(() => periodOptions(pickerGrain, max), [max, pickerGrain]);

  const supportsToday = useDeliverySiteScope();
  const visiblePresets = presets.filter(item => item.value !== 'today' || supportsToday);

  const selectCustomRange = (range: { start: string; end: string }) => {
    onRangeChange(range);
    if (preset !== customPreset) onPresetChange(customPreset);
  };

  return <div className={`marketing-time-range-filter${variant === 'reference' ? ' reference' : ''}`}>
    {grainLabel && <span className="data-filter-inline-label">{grainLabel}</span>}
    <div className="page-tabs marketing-time-grains" role="group" aria-label={grainLabel}>
      {grains.map(item => <button type="button" key={item.value} className={`page-tab${grain === item.value ? ' active' : ''}`} onClick={() => onGrainChange(item.value)}>{item.label}</button>)}
    </div>
    {rangeLabel && <span className="data-filter-inline-label">{rangeLabel}</span>}
    <div className="page-tabs marketing-time-presets" role="group" aria-label={rangeLabel || '日期范围'}>
      {visiblePresets.map(item => <button type="button" key={item.value} className={`page-tab${preset === item.value ? ' active' : ''}`} onClick={() => onPresetChange(item.value)}>{item.label}</button>)}
    </div>
    {(preset === customPreset || alwaysShowDatePicker) && <PeriodRangePicker
      grain={pickerGrain}
      start={start}
      end={end}
      max={max}
      options={options}
      formatValue={value => formatPeriod(value, pickerGrain)}
      normalizeValue={value => periodStart(value, pickerGrain)}
      onChange={selectCustomRange}
    />}
    <AdDataFreshnessNote />
  </div>;
}

interface MarketingDateRangePickerProps {
  start: string;
  end: string;
  max: string;
  onChange: (range: { start: string; end: string }) => void;
  grain?: MarketingTimeGrain;
}

export function MarketingDateRangePicker({ start, end, max, onChange, grain = 'day' }: MarketingDateRangePickerProps) {
  const pickerGrain = canonicalGrain(grain);
  const options = useMemo(() => periodOptions(pickerGrain, max), [max, pickerGrain]);
  return <PeriodRangePicker
    grain={pickerGrain}
    start={start}
    end={end}
    max={max}
    options={options}
    formatValue={value => formatPeriod(value, pickerGrain)}
    normalizeValue={value => periodStart(value, pickerGrain)}
    onChange={onChange}
  />;
}

export const MARKETING_RANGE_PRESETS = [
  { value: 'yesterday', label: '昨天' },
  { value: '7d', label: '近 7 天' },
  { value: '14d', label: '近 14 天' },
  { value: '30d', label: '近 30 天' },
  { value: 'thisWeek', label: '本周' },
  { value: 'thisMonth', label: '本月' },
  { value: 'custom', label: '自定义' },
] as const;

// 只有投放明细提供今日快捷项，概览继续使用通用预设。
export const DELIVERY_RANGE_PRESETS = [
  { value: 'today', label: '今天' },
  ...MARKETING_RANGE_PRESETS,
] as const;

export const CHANNEL_WIDE_RANGE_PRESETS = [
  { value: 'yesterday', label: '昨天' },
  { value: '7d', label: '近 7 天' },
  { value: '30d', label: '近 30 天' },
  { value: '90d', label: '近 90 天' },
  { value: 'thisWeek', label: '本周' },
  { value: 'thisMonth', label: '本月' },
  { value: 'custom', label: '自定义' },
] as const;
